import { useState, type FormEvent } from "react";
import { ShieldCheck, UserMinus, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { Button, Card, FieldError, Input, Label, PageHeader, Textarea } from "../components/ui";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { useRouter } from "../lib/router";

type RequestType = "criacao" | "desativacao";
type YesNo = "sim" | "nao" | "";

const ACCESS_TO_CREATE = [
  "Usuário no Helpdesk",
  "E-mail",
  "Assinatura do E-mail (Enviar o documento no Whatsapp)",
  "Login no computador / Windows",
  "Retirar o usuário antigo",
  "Google Slack",
  "Google Drive",
  "Pacote Office",
  "Leitor de PDF",
  "Teams",
  "Tarefia",
  "Antivirus",
  "VPN",
  "Impressora / Scanner",
  "Wallpaper",
];

const ACCESS_TO_REMOVE = [
  "Usuário no Helpdesk",
  "E-mail",
  "Pacote Office (caso possua)",
  "Backup do e-mail",
  "Google Slack",
  "VPN",
];

type FormState = {
  nomeSolicitante: string;
  emailSolicitante: string;
  empresaSolicitante: string;
  departamentoSolicitante: string;
  dataSolicitacao: string;
  tipo: RequestType;

  nomeColaborador: string;
  emailColaborador: string;
  departamentoColaborador: string;
  dataInicio: string;
  acessosCriar: string[];
  acessosCriarOutro: string;
  acessosPastas: string;

  nomeDesativado: string;
  emailDesativado: string;
  dataSaida: string;
  acessosRemover: string[];
  acessosRemoverOutro: string;
  backupEmail: YesNo;
  redirecionarEmail: YesNo;
  emailRedirecionamento: string;
  respostaAutomatica: YesNo;
  textoRespostaAutomatica: string;
};

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function emptyForm(empresa: string): FormState {
  return {
    nomeSolicitante: "",
    emailSolicitante: "",
    empresaSolicitante: empresa,
    departamentoSolicitante: "",
    dataSolicitacao: todayIso(),
    tipo: "criacao",
    nomeColaborador: "",
    emailColaborador: "",
    departamentoColaborador: "",
    dataInicio: "",
    acessosCriar: [],
    acessosCriarOutro: "",
    acessosPastas: "",
    nomeDesativado: "",
    emailDesativado: "",
    dataSaida: "",
    acessosRemover: [],
    acessosRemoverOutro: "",
    backupEmail: "",
    redirecionarEmail: "",
    emailRedirecionamento: "",
    respostaAutomatica: "",
    textoRespostaAutomatica: "",
  };
}

export function AccessRequestPage() {
  const { session } = useAuth();
  const { navigate } = useRouter();
  const [form, setForm] = useState<FormState>(() => emptyForm(session?.empresa ?? ""));
  const [errors, setErrors] = useState<Partial<Record<string, string>>>({});
  const [loading, setLoading] = useState(false);

  function setField<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((current) => ({ ...current, [key]: value }));
    if (errors[key]) setErrors((current) => ({ ...current, [key]: undefined }));
  }

  function toggleAccess(list: "acessosCriar" | "acessosRemover", item: string) {
    setForm((current) => {
      const has = current[list].includes(item);
      return {
        ...current,
        [list]: has ? current[list].filter((value) => value !== item) : [...current[list], item],
      };
    });
  }

  function validate() {
    const nextErrors: Record<string, string> = {};
    if (!form.nomeSolicitante.trim()) nextErrors.nomeSolicitante = "Informe o nome do solicitante.";
    if (!form.emailSolicitante.trim()) nextErrors.emailSolicitante = "Informe o e-mail do solicitante.";
    if (!form.departamentoSolicitante.trim()) nextErrors.departamentoSolicitante = "Informe o departamento.";
    if (!form.dataSolicitacao) nextErrors.dataSolicitacao = "Informe a data da solicitação.";

    if (form.tipo === "criacao") {
      if (!form.nomeColaborador.trim()) nextErrors.nomeColaborador = "Informe o nome do novo colaborador.";
      if (!form.emailColaborador.trim()) nextErrors.emailColaborador = "Informe o e-mail do colaborador.";
      if (!form.departamentoColaborador.trim()) nextErrors.departamentoColaborador = "Informe o departamento.";
      if (!form.dataInicio) nextErrors.dataInicio = "Informe a data de início.";
      if (!form.acessosCriar.length && !form.acessosCriarOutro.trim()) {
        nextErrors.acessosCriar = "Selecione ao menos um acesso.";
      }
      if (!form.acessosPastas.trim()) {
        nextErrors.acessosPastas = 'Informe os acessos às pastas ou "Não se aplica".';
      }
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!validate()) {
      toast.error("Revise os campos obrigatórios destacados.");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        tipo: form.tipo,
        nomeSolicitante: form.nomeSolicitante,
        emailSolicitante: form.emailSolicitante,
        departamentoSolicitante: form.departamentoSolicitante,
        dataSolicitacao: form.dataSolicitacao,
        nomeColaborador: form.nomeColaborador || null,
        emailColaborador: form.emailColaborador || null,
        departamentoColaborador: form.departamentoColaborador || null,
        dataInicio: form.dataInicio || null,
        acessosCriar: form.acessosCriar.length > 0 ? form.acessosCriar : null,
        acessosCriarOutro: form.acessosCriarOutro || null,
        acessosPastas: form.acessosPastas || null,
        nomeDesativado: form.nomeDesativado || null,
        emailDesativado: form.emailDesativado || null,
        dataSaida: form.dataSaida || null,
        acessosRemover: form.acessosRemover.length > 0 ? form.acessosRemover : null,
        acessosRemoverOutro: form.acessosRemoverOutro || null,
        backupEmail: form.backupEmail === "sim" ? true : form.backupEmail === "nao" ? false : null,
        redirecionarEmail: form.redirecionarEmail === "sim" ? true : form.redirecionarEmail === "nao" ? false : null,
        emailRedirecionamento: form.emailRedirecionamento || null,
        respostaAutomatica: form.respostaAutomatica === "sim" ? true : form.respostaAutomatica === "nao" ? false : null,
        textoRespostaAutomatica: form.textoRespostaAutomatica || null,
      };

      const result = await api.submitAccessRequest(payload);
      toast.success(`Solicitação registrada! Chamado #${result.chamadoId} aberto.`);
      navigate(`/chamados/${result.chamadoId}`);
    } catch {
      toast.error("Não foi possível registrar a solicitação. Tente novamente.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="page-stack max-w-[960px]">
      <PageHeader
        eyebrow="Administração"
        title="Solicitação de acesso"
        description="Criação ou desativação de usuários, direto pelo helpdesk. Nesta etapa o formulário é só para conferência — a gravação será feita em uma próxima etapa."
      />

      <form onSubmit={submit} className="space-y-5">
        <Card className="form-section">
          <div className="form-section-heading">
            <span>01</span>
            <div>
              <h2>Solicitação de desativação ou criação</h2>
              <p>Dados de quem está solicitando o acesso.</p>
            </div>
          </div>
          <div className="form-grid">
            <div>
              <Label htmlFor="req-nome">Nome do solicitante *</Label>
              <Input
                id="req-nome"
                value={form.nomeSolicitante}
                onChange={(event) => setField("nomeSolicitante", event.target.value)}
                aria-invalid={Boolean(errors.nomeSolicitante)}
              />
              <FieldError>{errors.nomeSolicitante}</FieldError>
            </div>
            <div>
              <Label htmlFor="req-email">E-mail do solicitante *</Label>
              <Input
                id="req-email"
                type="email"
                value={form.emailSolicitante}
                onChange={(event) => setField("emailSolicitante", event.target.value)}
                aria-invalid={Boolean(errors.emailSolicitante)}
              />
              <FieldError>{errors.emailSolicitante}</FieldError>
            </div>
            <div>
              <Label htmlFor="req-empresa">Empresa do solicitante</Label>
              <Input
                id="req-empresa"
                value={form.empresaSolicitante}
                readOnly
                className="bg-slate-50 cursor-default"
              />
            </div>
            <div>
              <Label htmlFor="req-departamento">Departamento do solicitante *</Label>
              <Input
                id="req-departamento"
                value={form.departamentoSolicitante}
                onChange={(event) => setField("departamentoSolicitante", event.target.value)}
                aria-invalid={Boolean(errors.departamentoSolicitante)}
              />
              <FieldError>{errors.departamentoSolicitante}</FieldError>
            </div>
            <div>
              <Label htmlFor="req-data">Data da solicitação *</Label>
              <Input
                id="req-data"
                type="date"
                value={form.dataSolicitacao}
                onChange={(event) => setField("dataSolicitacao", event.target.value)}
                aria-invalid={Boolean(errors.dataSolicitacao)}
              />
              <FieldError>{errors.dataSolicitacao}</FieldError>
            </div>
          </div>

          <div className="mt-2">
            <Label>Tipo de solicitação *</Label>
            <div className="radio-grid radio-grid--compact">
              <label>
                <input
                  type="radio"
                  name="tipo"
                  checked={form.tipo === "criacao"}
                  onChange={() => setField("tipo", "criacao")}
                />
                <span><UserPlus /> Criação de novo usuário</span>
              </label>
              <label>
                <input
                  type="radio"
                  name="tipo"
                  checked={form.tipo === "desativacao"}
                  onChange={() => setField("tipo", "desativacao")}
                />
                <span><UserMinus /> Desativação de usuário existente</span>
              </label>
            </div>
          </div>
        </Card>

        {form.tipo === "criacao" ? (
          <Card className="form-section">
            <div className="form-section-heading">
              <span>02</span>
              <div>
                <h2>Criação de novo usuário</h2>
                <p>Dados do colaborador que vai receber os acessos.</p>
              </div>
            </div>
            <div className="form-grid">
              <div>
                <Label htmlFor="col-nome">Nome completo do novo colaborador *</Label>
                <Input
                  id="col-nome"
                  value={form.nomeColaborador}
                  onChange={(event) => setField("nomeColaborador", event.target.value)}
                  aria-invalid={Boolean(errors.nomeColaborador)}
                />
                <FieldError>{errors.nomeColaborador}</FieldError>
              </div>
              <div>
                <Label htmlFor="col-email">E-mail do novo colaborador ou e-mail existente *</Label>
                <Input
                  id="col-email"
                  type="email"
                  value={form.emailColaborador}
                  onChange={(event) => setField("emailColaborador", event.target.value)}
                  aria-invalid={Boolean(errors.emailColaborador)}
                />
                <FieldError>{errors.emailColaborador}</FieldError>
              </div>
              <div>
                <Label htmlFor="col-departamento">Departamento do colaborador *</Label>
                <Input
                  id="col-departamento"
                  value={form.departamentoColaborador}
                  onChange={(event) => setField("departamentoColaborador", event.target.value)}
                  aria-invalid={Boolean(errors.departamentoColaborador)}
                />
                <FieldError>{errors.departamentoColaborador}</FieldError>
              </div>
              <div>
                <Label htmlFor="col-inicio">Data de início do colaborador *</Label>
                <Input
                  id="col-inicio"
                  type="date"
                  value={form.dataInicio}
                  onChange={(event) => setField("dataInicio", event.target.value)}
                  aria-invalid={Boolean(errors.dataInicio)}
                />
                <FieldError>{errors.dataInicio}</FieldError>
              </div>
            </div>

            <div className="mt-2">
              <Label>Quais acessos devem ser criados? *</Label>
              <div className="checkbox-grid">
                {ACCESS_TO_CREATE.map((item) => (
                  <label key={item}>
                    <input
                      type="checkbox"
                      checked={form.acessosCriar.includes(item)}
                      onChange={() => toggleAccess("acessosCriar", item)}
                    />
                    <span>{item}</span>
                  </label>
                ))}
              </div>
              <Input
                className="mt-2"
                placeholder="Outra (especifique)"
                value={form.acessosCriarOutro}
                onChange={(event) => setField("acessosCriarOutro", event.target.value)}
              />
              <FieldError>{errors.acessosCriar}</FieldError>
            </div>

            <div className="mt-4">
              <Label htmlFor="col-pastas">
                Acessos às pastas (caso exista, informe um usuário com os mesmos acessos) *
              </Label>
              <Input
                id="col-pastas"
                value={form.acessosPastas}
                onChange={(event) => setField("acessosPastas", event.target.value)}
                aria-invalid={Boolean(errors.acessosPastas)}
              />
              <FieldError>{errors.acessosPastas}</FieldError>
            </div>
          </Card>
        ) : (
          <Card className="form-section">
            <div className="form-section-heading">
              <span>03</span>
              <div>
                <h2>Desativação de usuário existente</h2>
                <p>Dados do colaborador que terá os acessos removidos.</p>
              </div>
            </div>
            <div className="form-grid">
              <div>
                <Label htmlFor="des-nome">Nome completo do colaborador desativado</Label>
                <Input
                  id="des-nome"
                  value={form.nomeDesativado}
                  onChange={(event) => setField("nomeDesativado", event.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="des-email">E-mail do colaborador a ser desativado</Label>
                <Input
                  id="des-email"
                  type="email"
                  value={form.emailDesativado}
                  onChange={(event) => setField("emailDesativado", event.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="des-saida">Data de saída do colaborador</Label>
                <Input
                  id="des-saida"
                  type="date"
                  value={form.dataSaida}
                  onChange={(event) => setField("dataSaida", event.target.value)}
                />
              </div>
            </div>

            <div className="mt-2">
              <Label>Quais acessos devem ser removidos?</Label>
              <div className="checkbox-grid">
                {ACCESS_TO_REMOVE.map((item) => (
                  <label key={item}>
                    <input
                      type="checkbox"
                      checked={form.acessosRemover.includes(item)}
                      onChange={() => toggleAccess("acessosRemover", item)}
                    />
                    <span>{item}</span>
                  </label>
                ))}
              </div>
              <Input
                className="mt-2"
                placeholder="Outra (especifique)"
                value={form.acessosRemoverOutro}
                onChange={(event) => setField("acessosRemoverOutro", event.target.value)}
              />
            </div>

            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div>
                <Label>Deve ser realizado o backup do e-mail?</Label>
                <div className="radio-grid radio-grid--compact">
                  <label>
                    <input
                      type="radio"
                      name="backupEmail"
                      checked={form.backupEmail === "sim"}
                      onChange={() => setField("backupEmail", "sim")}
                    />
                    <span>Sim</span>
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="backupEmail"
                      checked={form.backupEmail === "nao"}
                      onChange={() => setField("backupEmail", "nao")}
                    />
                    <span>Não</span>
                  </label>
                </div>
              </div>
              <div>
                <Label>O e-mail deve ser redirecionado para outro usuário?</Label>
                <div className="radio-grid radio-grid--compact">
                  <label>
                    <input
                      type="radio"
                      name="redirecionarEmail"
                      checked={form.redirecionarEmail === "sim"}
                      onChange={() => setField("redirecionarEmail", "sim")}
                    />
                    <span>Sim</span>
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="redirecionarEmail"
                      checked={form.redirecionarEmail === "nao"}
                      onChange={() =>
                        setForm((current) => ({
                          ...current,
                          redirecionarEmail: "nao",
                          emailRedirecionamento: "",
                        }))
                      }
                    />
                    <span>Não</span>
                  </label>
                </div>
              </div>
            </div>

            {form.redirecionarEmail === "sim" && (
              <div className="mt-4">
                <Label htmlFor="des-redirect-email">
                  Para qual e-mail as mensagens devem ser redirecionadas?
                </Label>
                <Input
                  id="des-redirect-email"
                  type="email"
                  value={form.emailRedirecionamento}
                  onChange={(event) => setField("emailRedirecionamento", event.target.value)}
                />
              </div>
            )}

            <div className="mt-4">
              <Label>Deve configurar resposta automática?</Label>
              <div className="radio-grid radio-grid--compact">
                <label>
                  <input
                    type="radio"
                    name="respostaAutomatica"
                    checked={form.respostaAutomatica === "sim"}
                    onChange={() => setField("respostaAutomatica", "sim")}
                  />
                  <span>Sim</span>
                </label>
                <label>
                  <input
                    type="radio"
                    name="respostaAutomatica"
                    checked={form.respostaAutomatica === "nao"}
                    onChange={() =>
                      setForm((current) => ({
                        ...current,
                        respostaAutomatica: "nao",
                        textoRespostaAutomatica: "",
                      }))
                    }
                  />
                  <span>Não</span>
                </label>
              </div>
            </div>

            {form.respostaAutomatica === "sim" && (
              <div className="mt-4">
                <Label htmlFor="des-resposta-texto">Texto da resposta automática</Label>
                <Textarea
                  id="des-resposta-texto"
                  value={form.textoRespostaAutomatica}
                  onChange={(event) => setField("textoRespostaAutomatica", event.target.value)}
                />
              </div>
            )}
          </Card>
        )}

        <Card className="form-section flex flex-row items-center justify-between gap-4">
          <p className="text-sm text-slate-500">
            A solicitação será registrada e poderá ser consultada pela equipe interna.
          </p>
          <Button type="submit" size="lg" disabled={loading}>
            <ShieldCheck className="size-4" />
            {loading ? "Registrando..." : "Registrar solicitação"}
          </Button>
        </Card>
      </form>
    </div>
  );
}
